import { Component, Input, Output, EventEmitter, OnInit, Inject } from 'ng-metadata/core';
import './youtube-video.component.scss';

/**
 * @ngdoc directive
 * @name mwContentDocumentYoutubeVideo
 * @module mw.components.contentDocuments
 *
 * @param {string} [video-url] - The URL of the video
 * @param {string} [video-id] - The ID of the video
 * @param {string} [display-mode] - The URL of the avatar
 * @param {boolean} [enable-playback=false] - Control is video iframe is included or not
 * @param {expression} [on-click] - Angular expression to evaluate on click of the media image or video. $event is the ng-click event.
 *
 * @description
 * `<mw-content-document-youtube-video>` provides a means for displaying a youtube video.
 *
 *  see https://www.labnol.org/internet/light-youtube-embeds/27941/ for some background info about the way this component is implemented
 *
 * @usage
 * <hljs hljs-language="html">
 *
 *   <!-- Display a video using 'videoUrl' input binding -->
 *   <mw-content-document-youtube-video video-url="http://somedomain/somevideo"></mw-content-document-youtube-video>
 *
 * </hljs>
 *
 */
 @Component({
    selector: 'mw-content-document-youtube-video',
    template: `
        <div ng-if="::$ctrl.videoId" class="mw-content-document-youtube-video mw-content-document-youtube-video--{{::$ctrl.displayMode}}">
            <div ng-if="$ctrl.embedUrl" class="mw-content-document-youtube-video__player">
                <a data-tracking-event="documentOperation/open" ng-if="::!$ctrl.enablePlayback" ng-href="{{::$ctrl.videoUrl}}" target="_blank" ng-click="$ctrl.handleClick($event)" class="mw-content-document-youtube-video__player-preview">
                     <img ng-if="::$ctrl.videoUrl && $ctrl.displayMode !== 'compact'" class="mw-content-document-youtube-video__player-preview-image" ng-src="{{::$ctrl.previewImgUrl}}"/>
                     <mw-content-document-image ng-if="::$ctrl.videoUrl && $ctrl.displayMode === 'compact'" image-url="::$ctrl.previewImgUrl" display-mode="::$ctrl.displayMode"></mw-content-document-image>
                     <div class="mw-content-document-youtube-video__player-preview-mask">
                        <div class="mw-content-document-youtube-video__player-preview-play-icon-triangle"></div>
                        <md-icon class="mw-content-document-youtube-video__player-preview-play-icon" md-svg-icon="meltwater:youtube"></md-icon>
                    </div>
                </a>
                <iframe ng-if="$ctrl.enablePlayback" class="mw-content-document-youtube-video__iframe" ng-src="{{::$ctrl.embedUrl | mwTrustUrl}}" frameborder="0" allowfullscreen allow="autoplay"></iframe>
            </div>
        </div>
    `
})
export class YoutubeVideoComponent implements OnInit {
    @Input('<') videoUrl: string;
    @Input('<') videoId: string;
    @Input('<') enablePlayback: boolean = false;
    @Input('<') displayMode: string = 'standard'; // Supported values 'standard'(default), 'compact'

    @Output() onClick = new EventEmitter<ng.IAngularEvent>();

    embedUrl: string = '';
    previewImgUrl: string = '';
    compactPreviewImgUrl: string = '';

    constructor(@Inject('$scope') private $scope) {
        'ngInject';
    }

    ngOnInit() {
        if (!this.videoId && this.videoUrl) {
            this.videoId = extractYoutubeIdFromUrl(this.videoUrl);
        }
        this.embedUrl = '//www.youtube.com/embed/' + this.videoId + '?autoplay=1&mute=1';
        this.previewImgUrl = '//img.youtube.com/vi/' + this.videoId + '/mqdefault.jpg';
        this.compactPreviewImgUrl = '//img.youtube.com/vi/' + this.videoId + '/hqdefault.jpg';
    }

    handleClick(event: ng.IAngularEvent) {
        if (this.displayMode === 'standard') {
            event.preventDefault();
            this.enablePlayback = true;
            this.onClick.emit(event);
        }
    }
}

function extractYoutubeIdFromUrl(url) {
    let id = null;

    if (url) {
        // hat tip to https://stackoverflow.com/questions/10591547/how-to-get-youtube-video-id-from-url
        id = url.match(/(?:https?:\/{2})?(?:w{3}\.)?youtu(?:be)?\.(?:com|be)(?:\/watch\?v=|\/)([^\s&]+)/);
        id = id ? id[1] : null;
    }

    return id;
}



// WEBPACK FOOTER //
// ./src/lib/components/content-documents/shared/media/youtube-video.component.ts